import {
	AfterViewInit,
	Component,
	inject,
	Inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	MatDialog,
	MAT_DIALOG_DATA,
	MatDialogTitle,
	MatDialogClose,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { Company } from 'src/app/models/data/company';
import { CompanyEntityRelationship } from 'src/app/models/data/entityRelationship';
import { ApiLoadingKeys, ApiService } from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { C } from '@angular/cdk/keycodes';
import { MatToolbar } from '@angular/material/toolbar';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
	MatCard,
	MatCardHeader,
	MatCardTitle,
	MatCardSubtitle,
} from '@angular/material/card';
import { AddressPipe } from '../../../../components/pipes';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetTableMenuComponent,
	DataSetRowClickDirective,
} from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { HasSystemCapabilityDirective } from '../../../../directives/system-capabilities/has-system-capability.directive';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { AnalyticsTrackingService } from '../../../../services/analytics-tracking.service';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-public-directory',
	templateUrl: './public-directory.component.html',
	styleUrls: ['./public-directory.component.scss', '../../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		DataSetTableMenuComponent,
		HasSystemCapabilityDirective,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		DataSetLoadingRowComponent,
	],
})
export class PublicDirectoryComponent
	implements AfterViewInit, OnDestroy, OnInit
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	displayedColumns: string[] = ['companyName', 'taxId', 'address', 'rowMenu'];
	dataSource = new MatTableDataSource<Company>();
	hasRequiredSc: boolean;
	/** CRM or GCM act — write path for Add To My Companies (not GCM View). */
	canAddToMyCompanies: boolean;
	/** GCM act or View — read path for opening a company from the overflow menu. */
	hasGcmView: boolean;
	loadingTable = false;
	isOracle = false;

	activeCompanyId?: string;
	private myCompaniesList: CompanyEntityRelationship[] = [];
	private readonly analytics = inject(AnalyticsTrackingService);

	fieldLabels = {
		defaultPlaceholder: $localize`:@@publicDirectory-defaultPlaceholder:Search company name...`,
		companyName: $localize`:@@publicDirectory-companyName:Company Name`,
		streetAddress: $localize`:@@publicDirectory-streetAddress:Street Address`,
		townCity: $localize`:@@publicDirectory-townCity:Town/City`,
		stateProvince: $localize`:@@publicDirectory-stateProvince:State/Province`,
		postalCode: $localize`:@@publicDirectory-postalCode:Postal Code`,
		country: $localize`:@@publicDirectory-country:Country`,
	};

	private filterSubscription?: Subscription;

	searchCompaniesPublicDirectoryLoadingKey =
		ApiLoadingKeys.searchCompaniesPublicDirectory;
	getCompanyRelationshipsLoadingKey = ApiLoadingKeys.getCompanyRelationships;
	finishedLoadingInitDataSearch = false;
	finishedLoadingInitDataCompanyRelationships = false;

	constructor(
		private apiService: ApiService,
		private dialog: MatDialog,
		private router: Router,
		private scService: SystemCapabilityService,
		private userProfileService: UserProfileService,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.hasRequiredSc = this.scService.hasOne([
			'globalCompanyManagement',
			'companyRelationshipManagement',
		]);
		this.canAddToMyCompanies = this.hasRequiredSc;
		this.hasGcmView = this.scService.hasGcmView();
	}

	async ngOnInit(): Promise<void> {
		this.analytics.logEvent('screen_view', {
			firebase_screen: 'PublicDirectory',
			firebase_screen_class: 'PublicDirectoryComponent',
		});

		const profile = await this.userProfileService.getUserProfile();
		this.activeCompanyId = profile.activeRoleDetails?.company?.id;

		this.isOracle = this.scService.isOracle;
		if (this.isOracle) {
			this.displayedColumns.splice(3, 0, 'slugId');
		}
	}

	ngAfterViewInit(): void {
		// TODO: Consider adding relationship information to searchCompaniesPublicDirectory if it exists.
		//       This would allow us to show in this list whether they have an active relationship, and also be smart
		//       about whether to show the `Add to List` option for a company.
		// DE4315: relationship information added if company is parent or grandparent
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result =
						await this.apiService.searchCompaniesPublicDirectory(filter);
					this.finishedLoadingInitDataSearch = true;
					return result;
				})
			)
			.subscribe((data) => {
				this.dataSource.data = data;
			});

		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.getCompanyRelationships(filter);
					this.finishedLoadingInitDataCompanyRelationships = true;
					return result;
				})
			)
			.subscribe((data) => {
				this.myCompaniesList = data;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
		this.dialog.closeAll();
	}

	onRowClick(company: Company): void {
		this.router.navigate(['/company', company.id, 'detail']);
	}

	navigateToAddCompanyById(company: Company): void {
		this.router.navigateByUrl('/add-company', {
			state: { company, hasUser: true },
		});
	}

	navigateToAddNewCompany(): void {
		this.router.navigateByUrl('/add-company');
	}

	isCompanyInMyCompaniesList(idToCheck?: string): boolean {
		return this.myCompaniesList.some((index) => index.company.id === idToCheck);
	}

	isAncestorOfUserActiveCompany(id?: string): boolean {
		const company = this.dataSource.data.find(
			(company: any) => company.id === id
		);
		if (company) {
			return company.ancestorOfCompany === true;
		}
		return false;
	}

	isThereOptionsToDisplay(idToCheck?: string): boolean {
		// Ancestors: no overflow (Add is N/A; open remains via row click)
		if (this.isAncestorOfUserActiveCompany(idToCheck)) {
			return false;
		}

		const canAdd =
			this.canAddToMyCompanies && !this.isCompanyInMyCompaniesList(idToCheck);

		// Support / GCM View: overflow offers View Company when Add is unavailable
		return canAdd || this.hasGcmView;
	}

	rowId(row: Company): string {
		return row?.id || 'unknown-id';
	}
}

@Component({
	selector: 'app-company-detail-dialog',
	templateUrl: '../company-detail-dialog.html',
	styleUrls: ['../../../common/main.scss'],
	imports: [
		MatToolbar,
		MatDialogTitle,
		MatButton,
		MatDialogClose,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		MatCard,
		MatCardHeader,
		MatCardTitle,
		MatCardSubtitle,
		MatDialogActions,
		AddressPipe,
	],
})
export class CompanyDetailDialogComponent {
	constructor(
		@Inject(MAT_DIALOG_DATA) public data: CompanyEntityRelationship,
		private router: Router
	) {}

	editClick(): void {
		this.router.navigate(['/company', this.data.company.id, 'detail']);
	}
}
